/**
 * App URLs. `circleId` is the member-facing circle identifier `<slug>-<key>`
 * (lib/circleIds.ts), or a bare slug on the legacy path (docs/architecture.md §6).
 */

/** Launcher-absolute path of an app: /c/<circleId>/<app>/ */
export function appPath(circleId: string, appSlug: string): string {
  return `/c/${circleId}/${appSlug}/`;
}

/**
 * Resolves an app-relative notification url ('/chores', '/') to the launcher-absolute
 * href (/c/<circleId>/<app>/chores). A missing url points at the app root.
 */
export function resolveNotificationUrl(circleId: string, appSlug: string, url?: string | null): string {
  const base = `/c/${circleId}/${appSlug}`;
  if (!url || url === '/') return `${base}/`;
  return `${base}${url.startsWith('/') ? url : `/${url}`}`;
}

/** App-relative urls must start with '/', may not contain '..' and must be short. */
export function isValidNotificationUrl(url: string): boolean {
  return url.length <= 500 && url.startsWith('/') && !url.startsWith('//') && !url.includes('..');
}

export type ParsedAppPath = { circleId: string; appSlug: string; rest: string };

/** `decodeURIComponent` that returns null instead of throwing on malformed escapes (`%E0%A4%A`). */
export function safeDecode(component: string): string | null {
  try {
    return decodeURIComponent(component);
  } catch {
    return null;
  }
}

/**
 * Parses /c/<circleId>/<app>/<rest...> (with or without trailing content).
 * Returns null when the path is not an app path (or a segment is not decodable);
 * `needsSlash` is true when the slash after the app slug is missing (caller should redirect).
 * `rest` is returned as-is (still percent-encoded); callers decode it with `safeDecode`.
 */
export function parseAppPath(pathname: string): (ParsedAppPath & { needsSlash: boolean }) | null {
  const m = /^\/c\/([^/]+)\/([^/]+)(\/.*)?$/.exec(pathname);
  if (!m) return null;
  const circleId = safeDecode(m[1]!);
  const appSlug = safeDecode(m[2]!);
  if (circleId === null || appSlug === null) return null;
  const tail = m[3];
  if (tail === undefined) return { circleId, appSlug, rest: '', needsSlash: true };
  return { circleId, appSlug, rest: tail.slice(1), needsSlash: false };
}
